import type { ComponentHealth, HealthState } from '@pa/api/clinic'
import { AlertTriangle, CircleCheck, CircleHelp, OctagonAlert } from 'lucide-react'
import { sinceText } from '../model/format'
import { COMPONENTS, STATE_LABEL } from '../model/labels'
import { uptimePct } from '../model/uptime'
import { UptimeBars } from './UptimeBars'

const STATE_ICON: Record<HealthState, typeof CircleCheck> = {
  up: CircleCheck,
  degraded: AlertTriangle,
  down: OctagonAlert,
  unknown: CircleHelp,
}

/** "Operational", "Degraded", "Down (since 4:23 PM)", "No data" (UserFlows C-12, API.md §6.16). */
export function stateText(c: Pick<ComponentHealth, 'state' | 'since'>, timeZone: string, now: number): string {
  if (c.state === 'down' && c.since) return `Down (since ${sinceText(c.since, timeZone, now)})`
  return STATE_LABEL[c.state]
}

/**
 * One C-12 component (UserFlows C-12): its name and help text, its state, the bars for the days shown (90, or 30 on
 * narrow screens) and "N days ago · XX.XX % uptime · Today" summed over those days. The session shows "PF Keeper
 * unreachable" with "No data" while the keeper is down (D-38).
 */
export function ComponentCard({
  component,
  days,
  timeZone,
  now,
  keeperDown = false,
}: {
  component: ComponentHealth
  /** How many of the trailing days to show. */
  days: number
  timeZone: string
  now: number
  keeperDown?: boolean
}) {
  const meta = COMPONENTS.find((c) => c.id === component.component)
  const shown = component.days.slice(-days)
  const pct = uptimePct(shown)
  const Icon = STATE_ICON[component.state]
  const unreachable = keeperDown && component.component === 'pf_session' && component.state === 'unknown'
  return (
    <div className="status-row">
      <div className="status-row-head">
        <b>{meta?.name}</b>
        <span className="help-dot" title={meta?.desc}>
          <CircleHelp aria-hidden />
          <span className="sr-only">{meta?.desc}</span>
        </span>
        <span className={`status-now is-${component.state}`}>
          <Icon aria-hidden /> {stateText(component, timeZone, now)}
          {unreachable && <span className="status-note"> · PF Keeper unreachable</span>}
        </span>
      </div>
      <UptimeBars days={shown} timeZone={timeZone} />
      <div className="uptime-axis">
        <span>{days} days ago</span>
        <i />
        <span className="pct">{pct === null ? 'No data' : `${pct} % uptime`}</span>
        <i />
        <span>Today</span>
      </div>
    </div>
  )
}
